import { EditableSnapQuote } from '@snap-types/actions/edit-snap-quote';
import { DocumentWithUser } from '@snap-types/models/document';

import { QuoteType } from '../../types/state/quote';
import { QuoteLineType } from '../../types/state/quote-line';
import { transformSnapQuoteLineToStoreState } from './transform-quote-line';

export function transformSnapQuoteToStoreState(quote: EditableSnapQuote): {
  quote: QuoteType;
} {
  const quoteLineUuids: string[] = [];
  const quoteLines: Record<string, QuoteLineType> = {};

  const documentIds: string[] = [];
  const documents: Record<string, DocumentWithUser> = {};

  for (const snapQuoteLine of quote.latestVersion.snapQuoteLines) {
    const { uuid, quoteLine } = transformSnapQuoteLineToStoreState(
      snapQuoteLine,
      quote.site.siteQuotingConfig,
    );

    quoteLineUuids.push(uuid);
    quoteLines[uuid] = quoteLine;
  }

  for (const document of quote.latestVersion.documents) {
    const { id, fileName, fileType, createdBy, createdAt } = document;
    documentIds.push(id);
    documents[id] = {
      id,
      fileName,
      fileType,
      createdBy: createdBy
        ? {
            displayName: createdBy.displayName,
          }
        : null,
      createdAt,
    };
  }

  return {
    quote: {
      siteUuid: quote.siteUuid,
      teamUuid: quote.createdByTeamUuid,

      editMode: quote.versions.length > 1 ? 'edit' : 'new',
      uuid: quote.uuid,
      versionUuid: quote.latestVersion.uuid,
      versionCount: quote.versions.length,
      alphaQuoteUuid: quote.latestVersion.alphaQuoteUuid ?? null,
      sentAt: quote.lastSentAt,
      quoteId: quote.quoteId,
      siteCode: quote.site.code,

      rootQuoteUuid: quote.rootQuoteUuid,
      currentAssignment: quote.currentAssignment,

      customerId: quote.customer?.customerId,
      customerIndustry: quote.customer?.industry ?? '',
      termsNetDays: quote.customer?.termsNetDays,
      sellingUnitOfMeasure: quote.site.siteQuotingConfig.sellingUnitOfMeasure,
      priceStrategies: quote.site.siteQuotingConfig.priceStrategies,
      decimalPlaces: quote.site.siteQuotingConfig.decimalPlaces,
      leadTimeFormat: quote.site.siteQuotingConfig.leadTimeFormat,
      productionDaysPerWeek: quote.site.siteQuotingConfig.productionDaysPerWeek,
      transitDaysPerWeek: quote.site.siteQuotingConfig.transitDaysPerWeek,
      planningDaysPerWeek: quote.site.siteQuotingConfig.planningDaysPerWeek,

      showCostFieldsForFixedPriceStrategies:
        quote.site.siteQuotingConfig.showCostFieldsForFixedPriceStrategies ??
        false,
      siteDiscountCodes: quote.site.siteDiscountCodes,
      allDiscountCodes: quote.site.allDiscountCodes ?? null,
      siteMarketCodes: quote.site.siteMarketCodes,
      allMarketCodes: quote.site.allMarketCodes ?? null,
      wonLossStatus: quote.latestVersion.wonLossStatus,

      name: quote.latestVersion.name,
      contactName: quote.latestVersion.contactName,
      contactEmail: quote.latestVersion.contactEmail,
      contactPhone: quote.latestVersion.contactPhone,
      customerContacts: quote.customer?.contacts,
      address1: quote.latestVersion.address1,
      address2: quote.latestVersion.address2,
      city: quote.latestVersion.city,
      state: quote.latestVersion.state,
      postalCode: quote.latestVersion.zipcode,
      countryCode: quote.latestVersion.country,

      customerQuoteId: quote.latestVersion.customerQuoteId,
      notes: quote.latestVersion.notes,
      specs: quote.latestVersion.specs,

      salesRepId: quote.latestVersion.salesRepId,
      salesRepName: quote.latestVersion.salesRepName,
      salesRepEmail: quote.latestVersion.salesRepEmail,
      salesRepPhone: quote.latestVersion.salesRepPhone,

      csrOid: quote.latestVersion.csrOid,
      csrName: quote.latestVersion.csrName,
      csrEmail: quote.latestVersion.csrEmail,

      daysUntilExpectedWin: quote.latestVersion.daysUntilExpectedWin,
      daysUntilQuoteExpires: quote.latestVersion.daysUntilQuoteExpires,
      quantityRange: quote.site.siteQuotingConfig.quantityRange,

      discountCode: quote.latestVersion.discountCode,
      marketCode: quote.latestVersion.marketCode,
      targetGrossMargin: quote.site.siteQuotingConfig.targetGrossMargin,
      targetContributionMargin:
        quote.site.siteQuotingConfig.targetContributionMargin,

      quoteLineUuids,
      quoteLines,

      documentIds,
      documents,
    },
  };
}

export function transformStoreStateToNestedQuoteLinesAndPrices(
  quoteLinesState: Record<string, QuoteLineType> | undefined,
) {
  if (!quoteLinesState) {
    return [];
  }

  return Object.values(quoteLinesState).map((line) => {
    const quotePrices = Object.values(line.quotePrices).map((quotePrice) => ({
      quantity: quotePrice.quantity,
      totalPrice: quotePrice.totalPrice,
    }));

    return {
      snapQuotePrices: quotePrices,
    };
  });
}
